// MyBallot.js
import React from 'react';
import { useTranslation } from 'react-i18next';

import useApi2Resource from '../../hooks/useApi2Resource';
import FeedItem from '../feed/FeedItem';
import { FeedButton, FeedCard, PanelState } from '../feed/FeedCard';
import { CONTEST, contestsForDistricts, fetchBallot } from '../../utils/ballot';
import { formatDollars } from '../../utils/spending';

/**
 * MyBallot
 *
 * The races for the districts picked in the elections panel's dropdowns, for
 * one level, Federal or State. At home those start on the districts the reader
 * saved -- the same districts the Representatives page uses -- but any district
 * can be picked, in any state. Read from /elections/ballot for the state's next
 * election date and matched by utils/ballot.
 *
 * Each contest is a heading -- the district in the state's own words where it
 * was saved that way -- over its candidates, incumbents first. A candidate is
 * a feed item: party and incumbency, the campaign's money where the FEC has
 * it, the campaign site, and More to the candidate page when there is an FEC
 * id to build it from.
 *
 * A district race with no district given is left out. The "pick your
 * district" line for it only appears when onPickDistricts is passed: the panel
 * passes no district for a chamber left on All, which is a choice, not a gap.
 *
 * Two things are said rather than hidden:
 *   - state candidates the ballot lists with no district cannot be placed in
 *     a race, so they are counted, with a way to the whole-state list;
 *   - a ballot longer than the API's limit says it was truncated.
 */
function MyBallot({ stateAbbr, date, level, location, onShowAll, onPickDistricts }) {
  const { t } = useTranslation('common');
  const ballot = useApi2Resource(() => fetchBallot(stateAbbr, date), [stateAbbr, date]);

  if (ballot.loading || ballot.error) {
    return (
      <FeedCard>
        <PanelState loading={ballot.loading} error={ballot.error} />
      </FeedCard>
    );
  }

  const { contests, unplaced, missingSeat } = contestsForDistricts(
    ballot.data?.candidates,
    location,
    level
  );

  const districtHeading = (contest) => {
    const n = contest.district;
    switch (contest.kind) {
      case CONTEST.US_SENATE:
        return t('common:usSenate', 'U.S. Senate');
      case CONTEST.US_HOUSE:
        return (
          location?.congressionalLabel ||
          (n
            ? `${t('common:usHouse', 'U.S. House')} \u00b7 ${t('common:district', 'District')} ${n}`
            : t('common:usHouse', 'U.S. House'))
        );
      case CONTEST.STATE_UPPER:
        return (
          location?.stateUpperLabel ||
          `${location?.stateUpperChamberName || t('common:stateSenate', 'State Senate')} \u00b7 ${t('common:district', 'District')} ${n}`
        );
      case CONTEST.STATE_LOWER:
        return (
          location?.stateLowerLabel ||
          `${location?.stateLowerChamberName || t('common:stateHouse', 'State House')} \u00b7 ${t('common:district', 'District')} ${n}`
        );
      default:
        return contest.office || t('common:statewideSeats', 'Statewide');
    }
  };

  const renderCandidate = (row, index) => {
    const incumbent = String(row.incumbent_challenger || '').toLowerCase() === 'incumbent';
    const raised = row.finance?.raised;
    const candidatePage = row.cand_id ? `/us/candidate/${encodeURIComponent(row.cand_id)}` : null;

    const details = [
      <span className="record-line">
        <span>{row.party || t('common:noPartyLabel', 'No party label')}</span>
        {incumbent && <span className="trade-tag">{t('common:incumbent', 'Incumbent')}</span>}
      </span>,
      typeof raised === 'number' ? (
        <span className="award-meta">
          {t('common:campaignRaised', 'Campaign raised {{amount}}', { amount: formatDollars(raised) })}
        </span>
      ) : null,
    ];

    return (
      <FeedItem
        key={`${row.cand_id || row.candidate_name}-${index}`}
        item={{
          id: row.cand_id || `${row.candidate_name}-${row.office}-${row.district || ''}`,
          title: row.candidate_name,
          created_at: null,
          url: row.campaign_website || row.official_source_url || null,
        }}
        feedName="Elections"
        branch="Elections"
        topic="Candidates"
        buttonName={row.campaign_website ? t('common:campaignSite', 'Campaign site') : t('common:source', 'Source')}
        schema=""
        table=""
        variant="default"
        details={details}
        shareUrl={candidatePage ? `https://news.govglance.org${candidatePage}` : row.campaign_website || null}
        showDate={false}
        moreUrl={candidatePage}
      />
    );
  };

  // A district race with no district given. Only raised when the caller asks
  // for it by passing onPickDistricts; otherwise the race is simply not shown.
  const needsDistrict =
    Boolean(onPickDistricts) &&
    ((level === 'federal' && missingSeat.includes(CONTEST.US_HOUSE)) ||
      (level === 'state' &&
        (missingSeat.includes(CONTEST.STATE_UPPER) || missingSeat.includes(CONTEST.STATE_LOWER))));

  return (
    <div className="ue-my-ballot">
      {needsDistrict && (
        <div className="ue-my-ballot-note">
          <span>
            {level === 'federal'
              ? t('common:pickCongressionalForHouse', 'Pick your congressional district to see your U.S. House race.')
              : t('common:pickLegislativeForState', 'Pick your state legislative districts to see those races.')}
          </span>
          <button type="button" className="ue-nudge" onClick={onPickDistricts}>
            {t('common:selectDistrict', 'Select your district')}
          </button>
        </div>
      )}

      {contests.length === 0 && !needsDistrict && (
        <FeedCard>
          <p className="feed-panel-state feed-panel-state--text">
            {t('common:noRacesForDistricts', 'No races for these districts are on this ballot.')}
          </p>
        </FeedCard>
      )}

      {contests.map((contest) => (
        <section key={contest.key} className="ue-contest">
          <h3 className="ue-contest-title">{districtHeading(contest)}</h3>
          {contest.candidates.map(renderCandidate)}
        </section>
      ))}

      {unplaced > 0 && (
        <div className="ue-my-ballot-note">
          <span>
            {t(
              'common:unplacedStateCandidates',
              '{{count}} state candidates are listed without a district, so they cannot be matched to this one.',
              { count: unplaced }
            )}
          </span>
          <FeedButton onClick={onShowAll}>{t('common:seeAllDistricts', 'See all districts')}</FeedButton>
        </div>
      )}

      {ballot.data?.truncated && (
        <p className="ue-my-ballot-note">
          {t('common:ballotTruncated', 'This ballot is longer than one answer holds, so some races may be missing.')}
        </p>
      )}

      <style>{`
        .ue-contest { margin-bottom: 8px; }
        .ue-contest-title {
          margin: 16px 0 8px;
          font-size: 13px;
          font-weight: 600;
          letter-spacing: 0.04em;
          text-transform: uppercase;
          color: var(--text-secondary);
        }
        .ue-my-ballot-note {
          display: flex;
          flex-wrap: wrap;
          align-items: center;
          gap: 8px 12px;
          margin: 8px 0 12px;
          font-size: 14px;
          color: var(--text-secondary);
        }
      `}</style>
    </div>
  );
}

export default MyBallot;